import { useState } from "react";
import { Button } from "./Button";
import { Container } from "./Container";
import { getConfig } from "../config/env";
import { webUrl } from "../config/web-routes";
import styles from "./AppFooter.module.css";
import { ThemeToggle } from "../theme/ThemeToggle";
import { useSession } from "../session/useSession";

/**
 * Sign-out (`APP-017`, moved here by `APP-022`).
 *
 * `APP-017` put this in the masthead, and its argument was a good one: it is the one control
 * a founder may need in a hurry, on a borrowed laptop, in front of a room, and the bottom of
 * a long desk is a poor place for that. What that argument did not survive is a phone. The
 * masthead row could not hold two controls, the identity and canon § 8's date line at 390px —
 * it did not wrap, so the date line squeezed everything beside it — and of the four things in
 * that row the date line is the one canon obliges. So the row keeps the action a founder came
 * to take and this moves down here, beside the theme control `APP-017` moved for the same
 * reason one task earlier.
 *
 * The hurry case is not abandoned, only relocated: the footer is one `End` key away, the
 * control is in the document's last landmark, and it is reachable by keyboard from anywhere
 * on the desk without passing through the desk's panels.
 *
 * Every sign-out that reaches core succeeds: `contracts/API.md` § Seller session shape has
 * the logout endpoint answer whether or not a session was found. What core cannot promise is
 * a request that never left the browser, and the session cookie is `HttpOnly`, so a founder
 * whose sign-out did not land is still signed in with a live cookie.
 *
 * So this says so, in the words the rest of the app uses for a request that did not arrive
 * (`src/lib/bid-messages.ts` and its siblings). It does not drop the founder onto the generic
 * unreachable panel, which reads as a signed-out screen and would tell exactly the person
 * this control exists for — someone on a borrowed laptop, about to walk away — that they are
 * safe when they are not. The button is disabled only for as long as the request is in
 * flight, so a second press cannot race the first, and pressing it again clears the notice
 * and retries.
 *
 * It renders only while there is a session to end, so the signed-out surface keeps its single
 * control (canon decision `D-008`).
 */
function SignOutControl() {
  const { state, signOut } = useSession();
  const [leaving, setLeaving] = useState(false);

  if (state.status !== "signed-in") return null;

  return (
    <Button
      variant="quiet"
      disabled={leaving}
      aria-busy={leaving}
      onClick={() => {
        setLeaving(true);
        void signOut().finally(() => {
          setLeaving(false);
        });
      }}
    >
      {leaving ? "Signing out…" : "Sign out"}
    </Button>
  );
}

/**
 * Says that the last sign-out did not happen.
 *
 * Deliberately *not* inside {@link SignOutControl}, which renders only while core has
 * confirmed a session. The case this exists for is the one where core cannot be reached at
 * all: the session read that follows the sign-out fails too, the app falls back to its
 * unreachable screen, and that screen is the one thing a founder must not read as "signed
 * out". The fact being reported is about the attempt, not about who is signed in, so it does
 * not wait for the session state to settle — and that independence is why it moved down here
 * as its own element rather than as a child of the control above.
 */
function SignOutFailedNotice() {
  const { signOutFailed } = useSession();
  if (!signOutFailed) return null;

  return (
    <p className={styles.signOutFailed} role="status">
      Dealbid couldn&rsquo;t be reached, so you were not signed out. Check your connection
      and try again.
    </p>
  );
}

export function AppFooter() {
  const { environment, apiBaseUrl } = getConfig();

  return (
    <footer className={styles.footer}>
      <Container className={styles.inner}>
        <hr className="boardRule" />
        {/*
          Canon CANON.md §5: promotional credit is not cash and cannot be withdrawn or
          transferred. The seller app is where that money-shaped number is spent, so the
          wording stays on every page rather than appearing once during onboarding.
        */}
        <p className={styles.terms}>
          Promotional credit is non-transferable platform credit for Dealbid placement
          services. It is not money, and it cannot be withdrawn or transferred. Winning a
          daily auction buys the next display day&rsquo;s sponsored position — it does not
          change how a deal was reviewed.
        </p>
        {/*
          Out to the public site, by name rather than by typed path (`APP-011`). Every
          target is an entry in `src/config/web-routes.ts`; see that file for why a wrong
          path here does not 404.

          "Deal rules" is the Disclosure policy. Of the two pages that could answer the
          label, it is the one that answers it directly: eligibility is decided before
          bidding, the bar is a real discount rather than a nominal one, and `Deal Verified`
          is stated with its limits. The What-is-Dealbid section explains the marketplace to
          a visitor who does not know it — which is not what a founder reading a footer in
          their own seller desk is asking.
        */}
        {/*
          The colour-theme control closes the footer's link line (`APP-017`), the way
          `dealbid-web`'s does (`WEB-008`). It was in the masthead, where a preference was
          spending width the two controls a founder acts on need on a phone. All three
          options stay visible rather than collapsing into a `select`, and
          `contracts/DESIGN-TOKENS.md` § Theme is unchanged by the move: `system` is still
          the default, still writes no `data-theme`, and the pre-paint script in
          `index.html` still prevents a wrong-theme flash.
        */}
        <div className={styles.bottom}>
          <p className={styles.links}>
            <a href={webUrl("howItWorks")}>How bidding works</a>
            <a href={webUrl("disclosure")}>Deal rules</a>
            <a href={webUrl("home")}>Public board</a>
          </p>
          {/*
            The preference and the session control, grouped at the end of the line
            (`APP-022`). They are grouped for layout and not because they are alike — one is
            how the desk looks and the other ends the visit — which is why sign-out keeps the
            quiet variant rather than an emphasis that would make it look like a footer's
            call to action.
          */}
          <div className={styles.controls}>
            <ThemeToggle />
            <SignOutControl />
          </div>
        </div>
        <SignOutFailedNotice />
        {environment === "production" ? null : (
          <p className={styles.build}>
            Environment: {environment} · API: {apiBaseUrl}
          </p>
        )}
      </Container>
    </footer>
  );
}
